import { PAYMENT_STATES } from "@cafe/shared";
import { Badge, Button, Card, DataTable, EmptyState, IconButton, PageHeader, Pagination, SearchInput, Select, Tooltip, useDebouncedValue } from "@cafe/ui";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { day, http, INVOICE_STATUS, money, monthLabel, STATUS } from "../lib/api";
import { CELL_STATUS, PAYMENT_STATE } from "../lib/labels";

type Cell = { month: string; status: string | null; amount: number; invoices: { id: string; number: string; status: string; total: number; dueDate: string; paidAt: string | null; kind: string }[] };
type Row = { companyId: string; name: string; plan: string; status: string; state: string; currency: string; billingPeriod: string; manager: string | null; nextDate: string | null; nextAmount: number | null; cells: Cell[] };
type Schedule = { months: string[]; rows: Row[]; counts: Record<string, number>; totals: { month: string; paid: number; open: number; overdue: number }[]; total: number };
const PAGE = 50;
const addMonths = (ym: string, k: number) => { const d = new Date(`${ym}-01T00:00:00Z`); d.setUTCMonth(d.getUTCMonth() + k); return d.toISOString().slice(0, 7); };

/** Табель оплат: клиенты по строкам, месяцы по столбцам; в клетке — счета месяца со статусом. Сверху — состояние клиентов по оплатам. */
export default function SchedulePage() {
  const navigate = useNavigate();
  const [from, setFrom] = useState<string | undefined>(undefined);
  const [state, setState] = useState("");
  const [manager, setManager] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const q = useDebouncedValue(search, 300);
  const staff = useQuery({ queryKey: ["staff-options"], queryFn: () => http.get<{ id: string; name: string }[]>("/staff/options") });
  const list = useQuery({
    queryKey: ["schedule", from, state, manager, q, page],
    queryFn: () => http.get<Schedule>("/payment-schedule", { from, months: 6, state: state || undefined, managerId: manager === "all" ? undefined : manager, q: q || undefined, page, pageSize: PAGE }),
    placeholderData: (prev) => prev,
  });
  const d = list.data;
  const first = d?.months[0];
  const reset = () => setPage(1);
  return (
    <>
      <PageHeader title="Табель оплат" description="Кто и за какой месяц заплатил. Месяц счёта — начало оплачиваемого периода; клик по клиенту — его карточка." />
      <div className="flex flex-wrap gap-2" role="group" aria-label="Состояние оплаты">
        <Button size="sm" variant={state === "" ? "primary" : "outline"} onClick={() => { setState(""); reset(); }}>Все · {d ? Object.values(d.counts).reduce((a, b) => a + b, 0) : "…"}</Button>
        {PAYMENT_STATES.map((s) => (
          <Tooltip key={s} content={PAYMENT_STATE[s]!.hint}>
            <Button size="sm" variant={state === s ? "primary" : "outline"} onClick={() => { setState(s); reset(); }}>{PAYMENT_STATE[s]!.label} · {d?.counts[s] ?? "…"}</Button>
          </Tooltip>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput value={search} onValueChange={(v) => { setSearch(v); reset(); }} placeholder="Название компании" className="sm:w-72" />
        <Select value={manager} onValueChange={(v) => { setManager(v); reset(); }} className="sm:w-56"
          options={[{ value: "all", label: "Все менеджеры" }, ...(staff.data ?? []).map((u) => ({ value: u.id, label: u.name }))]} />
        {first && (
          <div className="flex items-center gap-1 sm:ml-auto">
            <IconButton icon={<ChevronLeft />} aria-label="Раньше" onClick={() => setFrom(addMonths(first, -1))} />
            <span className="min-w-40 text-center text-sm">{monthLabel(first, true)} — {monthLabel(d.months[d.months.length - 1]!, true)}</span>
            <IconButton icon={<ChevronRight />} aria-label="Позже" onClick={() => setFrom(addMonths(first, 1))} />
          </div>
        )}
      </div>
      <Card className="p-2">
        <DataTable rows={d?.rows} loading={list.isPending} rowKey={(r) => r.companyId} onRowClick={(r) => navigate(`/companies/${r.companyId}`)}
          empty={<EmptyState icon={<CalendarDays />} title="Клиентов нет" description={state ? "С таким состоянием никого" : undefined} />}
          columns={[
            {
              key: "n", header: "Клиент", mobile: "title",
              cell: (r) => (
                <div className="min-w-44">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-muted">{r.plan} · {STATUS[r.status]?.label ?? r.status}{r.manager ? ` · ${r.manager}` : ""}</p>
                </div>
              ),
            },
            { key: "s", header: "Оплата", mobile: "subtitle", cell: (r) => <Badge tone={PAYMENT_STATE[r.state]?.tone ?? "neutral"} dot>{PAYMENT_STATE[r.state]?.label ?? r.state}</Badge> },
            {
              key: "next", header: "Следующий платёж",
              cell: (r) => <div className="tabular-nums"><p>{day(r.nextDate)}</p>{r.nextAmount !== null && <p className="text-xs text-muted">{money(r.nextAmount, r.currency)}</p>}</div>,
            },
            ...(d?.months ?? []).map((mo, mi) => ({
              key: mo, header: monthLabel(mo, true), align: "center" as const,
              cell: (r: Row) => {
                const c = r.cells[mi];
                if (!c?.status) return <span className="text-subtle">—</span>;
                return (
                  <Tooltip content={<div className="flex flex-col gap-0.5 text-xs">{c.invoices.map((i) => <span key={i.id}>{i.number}: {money(i.total, r.currency)} · {INVOICE_STATUS[i.status]?.label ?? i.status}{i.paidAt ? ` ${day(i.paidAt)}` : ` до ${day(i.dueDate)}`}</span>)}</div>}>
                    <span className="inline-flex flex-col items-center gap-0.5">
                      <Badge tone={CELL_STATUS[c.status]?.tone ?? "neutral"}>{CELL_STATUS[c.status]?.label ?? c.status}</Badge>
                      {c.amount > 0 && <span className="text-xs text-muted tabular-nums">{money(c.amount, r.currency)}</span>}
                    </span>
                  </Tooltip>
                );
              },
            })),
          ]} />
        {d && d.rows.length > 0 && (
          <div className="mt-2 grid gap-2 border-t border-border px-3 pt-3 text-xs sm:grid-cols-3 lg:grid-cols-6">
            {d.totals.map((t) => (
              <div key={t.month} className="flex flex-col gap-0.5 tabular-nums">
                <span className="font-medium">{monthLabel(t.month, true)}</span>
                <span className="text-success">оплачено {money(t.paid)}</span>
                {t.open > 0 && <span className="text-warning">ждём {money(t.open)}</span>}
                {t.overdue > 0 && <span className="text-danger">просрочено {money(t.overdue)}</span>}
              </div>
            ))}
          </div>
        )}
      </Card>
      {d && <Pagination page={page} pageSize={PAGE} total={d.total} onPageChange={setPage} />}
    </>
  );
}
